import { faArrowRight, faBarcode, faDownload, faForward, faRedo, faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton } from 'components';
import { ConfirmationPopup, TripUpdatePopup } from 'components/pop-up';
import { TempIntegrityCard } from 'components/temp-integrity';
import { TripContext } from 'contexts';
import { LoadIndicator, ScrollView } from 'devextreme-react';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useLoadManagerService, useNavigation, useParamService, useStopService, useTripService } from 'services';
import { OrderType, StopStatus, TripStatus } from 'services/enums';
import { Stop, TrailerTemperature, Trip, TripChange, TripUpdate } from 'services/types';
import './temp-integrity.scss';

export const TempIntegrityPage = () => {

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isPopUpVisible, setIsPopUpVisible] = useState<boolean>(false)
    const [isErrorVisible, setIsErrorVisible] = useState<boolean>(false)
    const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false)
    const [isVersionChecked, setIsVersionChecked] = useState<boolean>(false)
    const [popUpMessage, setPopUpMessage] = useState<string>('')
    const [errorMessage, setErrorMessage] = useState<string>('')
    const { setTrailerTemp, updateStopStatus } = useStopService()
    const { updateTripStatus } = useTripService()
    const { getIntValue } = useParamService
    const { getTripupdated, getLoadAssignments } = useLoadManagerService()
    const { navigateTo } = useNavigation()

    const stopId: number | undefined = getIntValue('stopId')
    const tripId: number | undefined = getIntValue('tripId')

    const context = useContext(TripContext)
    const trip: Trip = context.trips.find(trip => trip.id === tripId) ?? {} as Trip
    const stop: Stop = context.stops.find(stop => stop.id === stopId) ?? {} as Stop
    const noseTemp: TrailerTemperature = stop.trailerTemperatures?.nose ?? {} as TrailerTemperature
    const middleTemp: TrailerTemperature = stop.trailerTemperatures?.middle ?? {} as TrailerTemperature
    const tailTemp: TrailerTemperature = stop.trailerTemperatures?.tail ?? {} as TrailerTemperature

    const [changes, setChanges] = useState<TripChange[]>([])
    const [isViewingChanges, setIsViewingChanges] = useState<boolean>(false)

    const isSupplierPickup: boolean = stop != null && stop.orderTypes?.map(type => type.replace(/\s+/g, ' ').trim())?.some(type => type === OrderType.VendorPickup)

    useEffect(() => {
        const getLoads = async () => {
            try {
                await checkForLastUpdate()
            }
            catch (e) {
            }
            finally {
                setIsLoading(false)
            }
        }
        getLoads()
    }, [])

    const checkForLastUpdate = async () => {
        if (tripId != null && stopId != null) {
            try {
                const lastUpdated: TripUpdate = await getTripupdated(tripId)
                if (lastUpdated?.updated != null && new Date(lastUpdated.updated) > new Date(trip.updated)) {
                    setIsUpdateAvailable(true)
                    setIsVersionChecked(true)
                }
            }
            catch {
                setErrorMessage(`We ran into an issue while checking for updates to your load. Please ensure you have an internet connection.`)
                setIsErrorVisible(true)
            }
        }
    }

    const navigatePage = async () => {
        if (stop && stop.stopStatus == StopStatus.Pending)
            await updateStopStatus(stopId!, StopStatus.Started)
        if (trip && trip.status == TripStatus.Ready)
            await updateTripStatus(tripId!, TripStatus.Started)
        isSupplierPickup ? navigateTo(`supplier-pickup?tripId=${tripId}&stopId=${stopId}`, undefined, '/load-appointments')
            :
            navigateTo(`stop-overview?tripId=${tripId}&stopId=${stopId}&dry=${true}&frozen=${true}&ref=${true}`, undefined, '/load-appointments')
    }

    const isValid = (temp: TrailerTemperature): boolean => temp && (!temp.isSet || (temp.setValue != null && temp.actualValue != null))

    const store = (nose: TrailerTemperature, middle: TrailerTemperature, tail: TrailerTemperature): void => {
        setTrailerTemp(stop, nose, middle, tail)
    }

    const isValidTemp = (values: number[]): boolean => {
        return values.includes(noseTemp.setValue ?? -1) || values.includes(middleTemp.setValue ?? -1) || values.includes(tailTemp.setValue ?? -1)
    }

    const showMessage = (temp: string, values: number[]): void => {
        setIsPopUpVisible(true)
        setPopUpMessage(`There must be a zone with a set temperature of ${values.join(' or ')} for ${temp} product. Press Cancel to change the set temps or Proceed to continue`)
    }

    const onClick = async () => {
        const frozenTempValues = [0], refTempValues = [31, 34], fourthTempValues = [50, 70]
        const frozenTemp = 'Frozen', refTemp = 'Refrigerated', fourthTemp = 'Fourth Temp'

        if (stop.orderTemperatures.indexOf(refTemp) >= 0 && !isValidTemp(refTempValues)) {
            showMessage(refTemp, refTempValues)
        }
        else if (stop.orderTemperatures.indexOf(frozenTemp) >= 0 && !isValidTemp(frozenTempValues)) {
            showMessage(frozenTemp, frozenTempValues)
        }
        else if (stop.orderTemperatures.indexOf(fourthTemp) >= 0 && !isValidTemp(fourthTempValues)) {
            showMessage(fourthTemp, fourthTempValues)
        }
        else {
            navigatePage()
        }
    }

    const downloadTrip = async () => {
        try {
            setIsLoading(true)
            const newChanges: TripChange[] = await getLoadAssignments()
            if (newChanges.length > 0) {
                setChanges(newChanges)
                setIsViewingChanges(true)
            }
        }
        catch (e) {
            setErrorMessage(`We ran into an issue while updating your load. Please ensure you have an internet connection.`)
            setIsErrorVisible(true)
        }
        finally {
            setIsUpdateAvailable(false)
            setIsLoading(false)
        }
    }

    const onClickTryAgain = async () => {
        setIsErrorVisible(false)
        isVersionChecked ? await downloadTrip() : await checkForLastUpdate()
    }

    return (
        <Fragment>
            {isLoading ?
                <div className={'load-indicator'}>
                    <div>Updating to latest trip version</div>
                    <LoadIndicator width={64} height={64} />
                </div>
                :
                <Fragment>
                    <Fragment>
                        <ScrollView>
                            <TempIntegrityCard title='Nose Temperature' temperature={noseTemp} valueChanged={(newValue: TrailerTemperature) => store(newValue, middleTemp, tailTemp)} />
                            <TempIntegrityCard title='Middle Temperature' temperature={middleTemp} valueChanged={(newValue: TrailerTemperature) => store(noseTemp, newValue, tailTemp)} />
                            <TempIntegrityCard title='Tail Temperature' temperature={tailTemp} valueChanged={(newValue: TrailerTemperature) => store(noseTemp, middleTemp, newValue)} />
                        </ScrollView>
                        <div className='bottom-fix-action-row'>
                            <ActionButton
                                enabled={isValid(noseTemp) && isValid(middleTemp) && isValid(tailTemp)}
                                icon={isSupplierPickup ? faThumbsUp : faBarcode} buttonColor={'#76AB21'}
                                onClick={onClick}
                                buttonText={isSupplierPickup ? 'Verify Pickups' : 'Start Scanning'}
                                buttonWidth={'140px'}
                            />
                        </div>
                    </Fragment>
                    <ConfirmationPopup
                        isVisible={isPopUpVisible}
                        title="Warning"
                        message={popUpMessage}
                        button1={{ icon: faTimes, text: 'Cancel' }}
                        button2={{ icon: faArrowRight, text: 'Proceed' }}
                        onClickButton1={() => setIsPopUpVisible(false)}
                        onClickButton2={navigatePage}
                    />
                    <ConfirmationPopup
                        isVisible={isUpdateAvailable}
                        title="Update Available"
                        message={'There is an update available for this load.'}
                        button1={{ icon: faDownload, text: 'Update' }}
                        onClickButton1={() => downloadTrip()}
                    />
                    <ConfirmationPopup
                        isVisible={isErrorVisible}
                        title="Error"
                        message={errorMessage}
                        button1={{ icon: faRedo, text: 'Try again' }}
                        button2={{ icon: faForward, text: 'Skip' }}
                        onClickButton1={onClickTryAgain}
                        onClickButton2={() => setIsErrorVisible(false)}
                    />
                </Fragment>
            }
            <TripUpdatePopup
                isVisible={isViewingChanges}
                changes={changes}
                onClickOk={() => setIsViewingChanges(false)}
            />
        </Fragment>
    )
};
